export const focusAreas: Record<string, any> = {
  workflowAutomation: {
    title: "Intelligent Workflow Automation",
    description:
      "Transform your operations by automating end-to-end workflows that eliminate manual handoffs, reduce errors, and accelerate business processes from hours to minutes.",
    symptoms: [
      "Business processes require multiple manual handoffs",
      "Tasks sit in queues waiting for human action",
      "Simple processes take days when they should take minutes",
      "Team spending too much time on routine approvals and transfers",
    ],
    impacts: [
      "Slow response times hurting customer satisfaction",
      "High operational costs from manual labor",
      "Delays in critical business processes affecting revenue",
      "Employee frustration with repetitive, low-value work",
    ],
    solutions: [
      "Design automated workflows that handle routine processes end-to-end",
      "Implement intelligent routing that directs tasks to the right people automatically",
      "Deploy automated approval systems with business rule enforcement",
      "Create workflow templates for common business processes",
    ],
    nextSteps: [
      {
        step: "Map Current Workflows",
        action:
          "Document your most time-consuming manual processes to identify automation opportunities.",
      },
      {
        step: "Prioritize High-Volume Tasks",
        action:
          "Start with repetitive tasks that occur frequently and consume the most time.",
      },
      {
        step: "Design Automated Flows",
        action:
          "Create workflow automation for your top 3 most repetitive processes.",
      },
      {
        step: "Measure and Optimize",
        action:
          "Track time savings and continuously refine workflows based on performance data.",
      },
    ],
    insight:
      "Companies implementing workflow automation see 70% reduction in process completion time and 90% fewer errors, while freeing employees for strategic work.",
  },
  taskEfficiency: {
    title: "Smart Task Management",
    description:
      "Maximize productivity by automating task assignments, eliminating bottlenecks, and ensuring work flows smoothly through your organization without manual intervention.",
    symptoms: [
      "Work piles up in bottlenecks with unclear ownership",
      "Tasks fall through the cracks without clear tracking",
      "Too much time spent on task coordination and follow-up",
      "Difficulty prioritizing when everything seems urgent",
    ],
    impacts: [
      "Missed deadlines and commitments affecting reliability",
      "Team members overwhelmed or underutilized",
      "Management spending too much time on task coordination",
      "Poor visibility into actual workload and capacity",
    ],
    solutions: [
      "Implement automated task assignment based on availability and skills",
      "Deploy smart prioritization systems that route urgent work first",
      "Create automated escalation rules for overdue or stuck tasks",
      "Build workload balancing to distribute tasks evenly across teams",
    ],
    nextSteps: [
      {
        step: "Identify Bottlenecks",
        action:
          "Analyze where work gets stuck and why tasks are delayed in your current process.",
      },
      {
        step: "Define Assignment Rules",
        action:
          "Create clear criteria for automatic task routing based on skills, capacity, and priority.",
      },
      {
        step: "Automate Escalations",
        action:
          "Set up automated alerts and escalations for tasks approaching deadlines.",
      },
      {
        step: "Monitor Flow",
        action:
          "Track task completion rates and adjust automation rules to optimize throughput.",
      },
    ],
    insight:
      "Organizations using smart task automation report 50% faster task completion and 85% improvement in meeting deadlines, with managers saving 10+ hours weekly.",
  },
  integration: {
    title: "Seamless System Integration",
    description:
      "Connect your business systems to eliminate duplicate data entry, ensure information consistency, and create a unified operational environment where data flows automatically.",
    symptoms: [
      "Manually copying data between different systems",
      "Inconsistent information across business applications",
      "Time wasted switching between multiple tools",
      "Data sync issues causing errors and confusion",
    ],
    impacts: [
      "Data entry errors costing time and money",
      "Decisions made on outdated or incorrect information",
      "Frustrated employees dealing with system inefficiencies",
      "Lost productivity from system switching and data reconciliation",
    ],
    solutions: [
      "Build automated data synchronization between core business systems",
      "Implement unified dashboards that pull from all systems in real-time",
      "Create middleware that connects legacy systems with modern tools",
      "Deploy API integrations for seamless information flow",
    ],
    nextSteps: [
      {
        step: "Inventory Systems",
        action:
          "List all business systems and identify which ones need to share data.",
      },
      {
        step: "Map Data Flows",
        action:
          "Document what information needs to move between systems and how often.",
      },
      {
        step: "Connect Critical Paths",
        action:
          "Start by integrating your most important systems that are used daily.",
      },
      {
        step: "Validate Accuracy",
        action:
          "Implement automated data validation to ensure integration quality and consistency.",
      },
    ],
    insight:
      "Businesses with integrated systems eliminate 80% of duplicate data entry, reduce errors by 95%, and save employees 15-20 hours per week on manual data management.",
  },
};
